import {
  FinalStepperStep,
  FormField,
  FormFieldType,
  formFieldTypes,
  MultiFieldStepperStep,
  StepperForm,
  StepperStep,
} from '@betterfunnel/types';
import {
  ArrowUpRightIcon,
  Cog8ToothIcon,
  ComputerDesktopIcon,
  CursorArrowRaysIcon,
  CursorArrowRippleIcon,
  PlusIcon,
  SquaresPlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import {
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { uid } from 'uid';
import penDrawingLottie from '../../assets/lotties/pen-drawing.json';
import rocketFlyingLottie from '../../assets/lotties/rocket-flying.json';
import Autocomplete from '../../components/Common/Autocomplete';
import Button from '../../components/Common/Button';
import ChipsAutocomplete from '../../components/Common/ChipsAutocomplete';
import FullScreenModal from '../../components/Common/FullScreenModal';
import IconButton from '../../components/Common/IconButton';
import MenuButton, { MenuOption } from '../../components/Common/MenuButton';
import Select from '../../components/Common/Select';
import TextArea from '../../components/Common/TextArea';
import TextField from '../../components/Common/TextField';
import QuillField from '../../components/QuillField';
import SortableList from '../../components/SortableList';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import {
  createForm,
  generateStepperForm,
  updateForm,
} from '../../services/FormsService';
import {
  classNames,
  groupBy,
  PREVIEW_ROOT,
  sendMessageToIframe,
  STEPPER_PREVIEW_URL,
} from '../../utils';
import {
  getFormFieldTypeIcon,
  getFormFieldTypeLabel,
} from '../../utils/form-utils';
import { extractTextFromQuillTrimmed } from '../../utils/quill';
import { FormFieldItemForm } from '../CreateFormModal';

// Add React Flow imports
import dagre from '@dagrejs/dagre';
import {
  Background,
  Controls,
  Edge,
  Handle,
  MiniMap,
  Node,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CodeEditorTextArea } from '../../components/FormFields/CodeEditor/CodeEditorTextArea';

const textOperatorFields: FormFieldType[] = [
  'text',
  'tel',
  'email',
  'textArea',
  'businessEmail',
  'country',
  'state',
];

const textOperators: { value: string; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Not Contains' },
  { value: 'isEmpty', label: 'Is Empty' },
];

const numberOperatorFields: FormFieldType[] = ['number'];
const numberOperators: { value: string; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'greaterThan', label: 'Greater Than' },
  { value: 'lessThan', label: 'Less Than' },
  { value: 'greaterThanOrEqualTo', label: 'Greater Than Or Equal To' },
  { value: 'lessThanOrEqualTo', label: 'Less Than Or Equal To' },
];

const selectOparatorFields: FormFieldType[] = [
  'select',
  'multiChoice',
  'pictureChoice',
];

const selectOparators: { value: string; label: string }[] = [
  { value: 'isAny', label: 'Is Any' },
  { value: 'hasAll', label: 'Has All Of' },
  { value: 'hasAll', label: 'Has None Of' },
  { value: 'isEmpty', label: 'Is Empty' },
];

const tabs = ['editor', 'logic', 'preview', 'settings'] as const;

type StepperEditorTab = (typeof tabs)[number];

const tabsWithIcons: Record<
  StepperEditorTab,
  { icon: ReactNode; label: string }
> = {
  editor: {
    icon: <SparklesIcon className="size-4" />,
    label: 'Editor',
  },
  logic: {
    icon: <CursorArrowRaysIcon className="size-4" />,
    label: 'Logic',
  },
  preview: {
    icon: <ComputerDesktopIcon className="size-4" />,
    label: 'Preview',
  },
  settings: {
    icon: <Cog8ToothIcon className="size-4" />,
    label: 'Settings',
  },
};

interface Props {
  form?: StepperForm;
  formId?: string;
  open: boolean;
  onClose: () => void;
}

const defaultForm = (): StepperForm => ({
  name: '',
  formType: 'stepper',
  createdAt: new Date(),
  updatedAt: new Date(),
  createdBy: '',
  status: 'active',
  steps: [
    {
      id: uid(),
      title: '',
      fields: [],
    },
  ],
  finalSteps: [
    {
      id: uid(),
      title: 'Thank You',
      stepType: 'confirmation',
    },
  ],
  captcha: {
    enabled: false,
  },
});

export default function EditStepperFormModal({
  open,
  onClose,
  form,
  formId,
}: Props) {
  const { show } = useSnackbarContext();
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<any>(null);
  const [data, setData] = useState<StepperForm | null>(() => form || null);
  const [tab, setTab] = useState<'editor' | 'logic' | 'preview' | 'settings'>(
    'editor',
  );

  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(
    null,
  );

  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(
    null,
  );

  const [selectedFinalStepIndex, setSelectedFinalStepIndex] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (form) {
      setData({ ...form });
    } else if (open) {
      setData(null);
    }
    setError(null);
  }, [form, open]);

  useEffect(() => {
    setSelectedStepIndex((curr) => {
      if (!data) return null;
      if (curr !== null && curr < data.steps.length) {
        return curr;
      }

      return data.steps.length > 0 ? 0 : null;
    });
  }, [data]);

  const verifyField = useCallback(
    (field: FormField): boolean => {
      if (!field.label?.trim()) {
        show('Field label is required');
        return false;
      }
      if (!field.id?.trim()) {
        show('Field id is required');
        return false;
      }
      if (!/^[A-Za-z0-9]+$/.test(field.id)) {
        show('Field id can only contain letters and numbers');
        return false;
      }
      if (field.type === 'select' && !field.options?.length) {
        show('Select field needs at least one option');
        return false;
      }
      if (field.type === 'select' && field.options?.length) {
        const hasEmptyOption = field.options.some((o) => {
          if (typeof o === 'string') {
            return o.trim() === '';
          }
          return !o.label?.trim() || !o.value?.trim();
        });
        if (hasEmptyOption) {
          show('Select field has empty options');
          return false;
        }
      }
      if (field.type === 'multiChoice' && !field.options?.length) {
        show('Multi choice field needs at least one option');
        return false;
      }
      if (field.type === 'multiChoice' && field.options?.length) {
        const hasEmptyOption = field.options.some((o) => {
          return !o.label?.trim() || !o.value?.trim();
        });
        if (hasEmptyOption) {
          show('Multi choice field has empty options');
          return false;
        }
      }
      if (field.type === 'pictureChoice' && !field.options?.length) {
        show('Picture choice field needs at least one option');
        return false;
      }
      if (field.type === 'pictureChoice' && field.options?.length) {
        const hasEmptyOption = field.options.some((o) => {
          return !o.label?.trim() || !o.value?.trim();
        });
        if (hasEmptyOption) {
          show('Picture choice field has empty options');
          return false;
        }
      }

      return true;
    },
    [show],
  );

  const verifyStep = useCallback(
    (step: StepperStep) => {
      if (!extractTextFromQuillTrimmed(step.title)) {
        show('Step title is required');
        return false;
      }
      if (step.stepType === 'loading') {
        return true;
      }
      if (!step.fields?.length) {
        show('You need to add at least one field');
        return false;
      }
      if (
        Object.values(
          groupBy(step.fields?.map((f) => f.id) || [], (a) => a),
        ).some((x) => x.length > 1)
      ) {
        show('You have multiple fields with the same id');
        return false;
      }
      for (const field of step.fields) {
        const isValid = verifyField(field);
        if (!isValid) {
          return false;
        }
      }
      return true;
    },
    [show, verifyField],
  );

  const verifyForm = useCallback(
    (form: StepperForm) => {
      if (!form.name?.trim()) {
        show('Name is required');
        return false;
      }
      if (!form.steps?.length) {
        show('You need to add at least one step');
        return false;
      }
      for (const step of form.steps) {
        const isValid = verifyStep(step);
        if (!isValid) {
          return false;
        }
      }
      return true;
    },
    [show, verifyStep],
  );

  const handleSave = useCallback(async () => {
    if (!selectedAccount || !data) return;

    try {
      const form: StepperForm = {
        ...data,
        createdBy: firebaseUser?.uid || '',
        updatedAt: new Date(),
      };

      if (!verifyForm(form)) {
        return;
      }

      setSaving(true);

      if (formId) {
        await updateForm(selectedAccount.id, formId, form);
      } else {
        await createForm(selectedAccount.id, form);
      }
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e.message || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  }, [data, firebaseUser?.uid, formId, onClose, selectedAccount, verifyForm]);

  return (
    <FullScreenModal open={open} onClose={() => {}}>
      <div className="flex h-screen w-full flex-col bg-gray-50">
        <div className="flex items-center justify-between gap-4 bg-white px-4 pb-4 pt-5 sm:px-6">
          <div className="flex items-center gap-4">
            <IconButton variant="ternary" onClick={onClose}>
              <XMarkIcon className="size-5" />
            </IconButton>
            {data && (
              <TextField
                placeholder="New Stepper"
                className="text-xl shadow-none ring-0"
                value={data.name}
                onChange={(e) => {
                  setData((prev) =>
                    prev ? { ...prev, name: e.target.value } : null,
                  );
                }}
              />
            )}
          </div>

          {data && (
            <div className="flex items-stretch gap-4">
              {error ? (
                <div className="mt-5 w-full text-end text-red-600">
                  {error.message || 'Failed to create form'}
                </div>
              ) : (
                <></>
              )}
              <Button size="large" onClick={handleSave} disabled={saving}>
                Save
              </Button>
            </div>
          )}
        </div>
        {data && (
          <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col overflow-hidden px-4">
            <div className="flex items-center gap-2 px-2 py-3">
              {tabs.map((tabId) => (
                <Button
                  key={tabId}
                  color={tab === tabId ? 'white' : 'default'}
                  variant={tab === tabId ? 'primary' : 'ternary'}
                  icon={tabsWithIcons[tabId].icon}
                  onClick={() => setTab(tabId)}
                >
                  {tabsWithIcons[tabId].label}
                </Button>
              ))}
            </div>
            <div className="flex-1 overflow-hidden">
              {tab === 'editor' && (
                <EditorTab
                  data={data}
                  setData={setData}
                  selectedStepIndex={selectedStepIndex}
                  setSelectedStepIndex={(i) => {
                    setSelectedStepIndex(i);
                    setSelectedFinalStepIndex(null);
                  }}
                  selectedFieldIndex={selectedFieldIndex}
                  setSelectedFieldIndex={setSelectedFieldIndex}
                  selectedFinalStepIndex={selectedFinalStepIndex}
                  setSelectedFinalStepIndex={setSelectedFinalStepIndex}
                />
              )}

              {tab === 'logic' && <LogicTab data={data} setData={setData} />}

              {tab === 'preview' && <PreviewTab data={data} formId={formId} />}

              {tab === 'settings' && (
                <SettingsTab data={data} setData={setData} />
              )}
            </div>
          </div>
        )}

        {!data && (
          <GenerateForm
            onFormGenerated={(form) => {
              setData(form);
            }}
          />
        )}
      </div>
    </FullScreenModal>
  );
}

interface EditorTabProps {
  data: StepperForm;
  setData: (_: SetStateAction<StepperForm | null>) => void;
  selectedStepIndex: number | null;
  setSelectedStepIndex: (index: number) => void;
  selectedFieldIndex?: number | null;
  setSelectedFieldIndex: (_: SetStateAction<number | null>) => void;
  selectedFinalStepIndex: number | null;
  setSelectedFinalStepIndex: (_: SetStateAction<number | null>) => void;
}

const EditorTab = ({
  data,
  setData,
  selectedStepIndex,
  setSelectedStepIndex,
  selectedFieldIndex,
  setSelectedFieldIndex,
  selectedFinalStepIndex,
  setSelectedFinalStepIndex = () => {},
}: EditorTabProps) => {
  const [editId, setEditId] = useState<string>(uid(8));

  const selectedStep = useMemo(() => {
    if (selectedFinalStepIndex !== null) {
      return data?.finalSteps[selectedFinalStepIndex] || null;
    }

    if (selectedStepIndex !== null) {
      return data?.steps[selectedStepIndex] || null;
    }
    return null;
  }, [
    selectedFinalStepIndex,
    selectedStepIndex,
    data?.finalSteps,
    data?.steps,
  ]);

  const selectedField = useMemo(() => {
    if (!selectedStep) return null;
    if (selectedStep.stepType === 'loading') return null;
    if (selectedStep.stepType && selectedStep.stepType !== 'multifield') {
      return null;
    }

    return selectedStep.fields[selectedFieldIndex || 0];
  }, [selectedFieldIndex, selectedStep]);

  useEffect(() => {
    setSelectedFieldIndex((curr) => {
      if (!selectedStep) return null;
      if (selectedStep.stepType === 'loading') return null;
      if (selectedStep.stepType && selectedStep.stepType !== 'multifield') {
        return null;
      }
      if (curr !== null && curr < selectedStep.fields.length) {
        return curr;
      }

      return selectedStep.fields.length > 0 ? 0 : null;
    });
  }, [selectedStep, setSelectedFieldIndex]);

  return (
    <div
      className="grid h-full w-full gap-x-2 overflow-hidden px-2 pb-2"
      style={{ gridTemplateColumns: '300px 1fr 300px' }}
    >
      <CardWrapper>
        <StepsList
          data={data}
          setData={setData}
          setEditId={setEditId}
          selectedStep={selectedStep}
          setSelectedStepIndex={setSelectedStepIndex}
          setSelectedFinalStepIndex={setSelectedFinalStepIndex}
        />
      </CardWrapper>
      <CardWrapper>
        {selectedStepIndex !== null && selectedStep && (
          <EditStep
            key={selectedStep.id + editId}
            data={data}
            index={selectedStepIndex + 1}
            step={selectedStep}
            allSteps={data.steps}
            selectedField={selectedField}
            onFieldSelected={(fieldIndex) => {
              setSelectedFieldIndex(fieldIndex);
            }}
            onChange={(step) => {
              if (selectedFinalStepIndex !== null) {
                setData((prev) =>
                  prev
                    ? {
                        ...(prev as any),
                        finalSteps: prev.finalSteps.map((s, i) =>
                          i === selectedFinalStepIndex ? step : s,
                        ),
                      }
                    : null,
                );
              } else {
                setData((prev) =>
                  prev
                    ? {
                        ...prev,
                        steps: prev.steps.map((s, i) =>
                          i === selectedStepIndex ? step : s,
                        ),
                      }
                    : null,
                );
              }
            }}
          />
        )}
      </CardWrapper>
      <CardWrapper>
        {selectedField && (
          <EditField
            field={selectedField}
            fields={
              !selectedStep?.stepType || selectedStep.stepType === 'multifield'
                ? selectedStep?.fields || []
                : []
            }
            onChange={(field) => {
              setData((prev) =>
                prev
                  ? {
                      ...prev,
                      steps: prev.steps.map((s, i) =>
                        i === selectedStepIndex && s.stepType !== 'loading'
                          ? {
                              ...s,
                              fields: s.fields.map((f) =>
                                f.id === field._id ? field : f,
                              ),
                            }
                          : s,
                      ),
                    }
                  : null,
              );
            }}
          />
        )}
      </CardWrapper>
    </div>
  );
};

const CardWrapper = ({ children }: { children?: ReactNode }) => {
  return (
    <div className="h-full overflow-scroll bg-white shadow-md sm:rounded-lg">
      <div className="flex min-h-full flex-col px-3 py-2">{children}</div>
    </div>
  );
};

interface StepsListProps {
  data: StepperForm;
  setData: (_: SetStateAction<StepperForm | null>) => void;
  setEditId: (id: string) => void;
  selectedStep: StepperStep | FinalStepperStep | null;
  setSelectedStepIndex: (index: number) => void;
  setSelectedFinalStepIndex: (_: SetStateAction<number | null>) => void;
}

const StepsList = ({
  data,
  setData,
  setEditId,
  selectedStep,
  setSelectedStepIndex,
  setSelectedFinalStepIndex,
}: StepsListProps) => {
  return (
    <>
      <div className="flex-1">
        <div className="flex items-center justify-between pb-2">
          <label
            htmlFor="email"
            className="text-md block font-medium leading-8 text-gray-900"
          >
            Steps
          </label>

          <MenuButton
            customButton={
              <IconButton variant="ternary" className="-me-1">
                <PlusIcon className="h-4 w-4" />
              </IconButton>
            }
            items={(
              [
                ...formFieldTypes.map((type) => ({
                  id: type,
                  title: getFormFieldTypeLabel(type),
                  icon: getFormFieldTypeIcon(type),
                })),
                { id: 'divider', type: 'divider' },
                {
                  id: 'empty',
                  title: 'Blank',
                  icon: <SquaresPlusIcon className="h-4 w-4" />,
                },
                {
                  id: 'loading',
                  title: 'Loading',
                  icon: <ArrowPathIcon className="h-4 w-4" />,
                },
              ] satisfies MenuOption[]
            ).map((item) => ({
              ...item,
              onClick: () => {
                setData((prev) =>
                  prev
                    ? {
                        ...prev,
                        steps: [
                          ...prev.steps,
                          item.id === 'loading'
                            ? {
                                id: uid(),
                                title: `Step ${data.steps.length + 1}`,
                                stepType: 'loading',
                                loadingText: 'Loading...',
                                loadingDuration: 3,
                              }
                            : {
                                id: uid(),
                                title: `Step ${data.steps.length + 1}`,
                                fields: formFieldTypes.includes(
                                  item.id as FormFieldType,
                                )
                                  ? [
                                      {
                                        id: uid(),
                                        type: item.id as FormFieldType,
                                        label: getFormFieldTypeLabel(
                                          item.id as FormFieldType,
                                        ),
                                      },
                                    ]
                                  : [],
                              },
                        ],
                      }
                    : null,
                );

                setTimeout(() => {
                  setSelectedStepIndex(data.steps.length);
                }, 0);
              },
            }))}
          />
        </div>
        <SortableList
          hideDragHandle
          hideDivider
          items={data.steps}
          onChange={(items) => {
            setData((prev) => (prev ? { ...prev, steps: items } : null));
          }}
          renderItemContent={(item, index) => (
            <div
              className={classNames(
                'group',
                'flex w-full cursor-pointer items-center justify-between rounded-md p-2 ring-1 ring-gray-200 transition hover:ring-2 hover:ring-blue-500',
                selectedStep?.id === item.id
                  ? 'bg-gray-200'
                  : 'hover:bg-gray-50',
              )}
              onClick={() => {
                setSelectedStepIndex(index);
              }}
            >
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-6 min-w-6 items-center justify-center rounded-md bg-gray-100">
                  <span className="text-xs font-extrabold text-gray-600">
                    {(index + 1).toString().padStart(2, '0')}
                  </span>
                </div>
                <div className="flex flex-1 flex-col">
                  <span className="text-gray-900">Step {index + 1}</span>
                  {item.title ? (
                    <span className="text-gray-600">
                      {extractTextFromQuillTrimmed(item.title)}
                    </span>
                  ) : undefined}
                </div>
              </div>

              <IconButton
                color="negative"
                variant="ternary"
                className="absolute right-2 hidden group-hover:block"
                onClick={(e) => {
                  e.stopPropagation();
                  setData((prev) =>
                    prev
                      ? {
                          ...prev,
                          steps: prev.steps.filter((s) => s.id !== item.id),
                        }
                      : null,
                  );
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          )}
        />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between pb-2 pt-2">
          <label
            htmlFor="email"
            className="text-md block font-medium leading-8 text-gray-900"
          >
            Final Steps
          </label>

          <MenuButton
            customButton={
              <IconButton variant="ternary" className="-me-1">
                <PlusIcon className="h-4 w-4" />
              </IconButton>
            }
            items={(
              [
                {
                  id: 'confirmation',
                  title: 'Thank You',
                  icon: <SquaresPlusIcon className="h-4 w-4" />,
                },
                {
                  id: 'redirect',
                  title: 'Redirect',
                  icon: <ArrowUpRightIcon className="h-4 w-4" />,
                },
                {
                  id: 'customCode',
                  title: 'Custom Code',
                  icon: <ComputerDesktopIcon className="h-4 w-4" />,
                },
                {
                  id: 'iframe',
                  title: 'IFrame',
                  icon: <CursorArrowRippleIcon className="h-4 w-4" />,
                },
              ] satisfies MenuOption[]
            ).map((item) => ({
              ...item,
              onClick: () => {
                setData((prev) =>
                  prev
                    ? {
                        ...prev,
                        finalSteps: [
                          ...(prev.finalSteps || []),
                          {
                            id: uid(),
                            title: `Final Step ${(data.finalSteps || []).length + 1}`,
                            stepType: item.id as any,
                            ...(item.id === 'redirect'
                              ? { redirectUrl: '' }
                              : {}),
                          },
                        ],
                      }
                    : null,
                );

                setTimeout(() => {
                  setSelectedFinalStepIndex(data.finalSteps.length);
                }, 0);
              },
            }))}
          />
        </div>
        <SortableList
          hideDragHandle
          hideDivider
          items={data.finalSteps || []}
          onChange={(items) => {
            setData((prev) => (prev ? { ...prev, finalSteps: items } : null));
          }}
          renderItemContent={(item, index) => (
            <div
              className={classNames(
                'group',
                'flex w-full cursor-pointer items-center justify-between rounded-md p-2 ring-1 ring-gray-200 transition hover:ring-2 hover:ring-blue-500',
                selectedStep?.id === item.id
                  ? 'bg-gray-200'
                  : 'hover:bg-gray-50',
              )}
              onClick={() => {
                setSelectedFinalStepIndex(index);
              }}
            >
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-6 min-w-6 items-center justify-center rounded-md bg-gray-100">
                  <span className="text-xs font-extrabold text-gray-600">
                    {(index + 1).toString().padStart(2, '0')}
                  </span>
                </div>
                <div className="flex flex-1 flex-col">
                  <span className="text-gray-900">Final Step {index + 1}</span>
                  {item.title ? (
                    <span className="text-gray-600">
                      {extractTextFromQuillTrimmed(item.title)}
                    </span>
                  ) : undefined}
                </div>
              </div>

              <IconButton
                color="negative"
                variant="ternary"
                className="absolute right-2 hidden group-hover:block"
                onClick={(e) => {
                  e.stopPropagation();
                  setData((prev) =>
                    prev
                      ? {
                          ...prev,
                          finalSteps: prev.finalSteps.filter(
                            (s) => s.id !== item.id,
                          ),
                        }
                      : null,
                  );
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          )}
        />
      </div>
      <PromptBox
        data={data}
        onGenerated={(generated) => {
          setEditId(uid());
          setData((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              ...generated,
            };
          });
        }}
      />
    </>
  );
};

interface EditStepProps {
  data: StepperForm;
  index: number;
  step: StepperStep | FinalStepperStep;
  allSteps: StepperStep[];
  selectedField: FormField | null;
  onFieldSelected: (fieldIndex: number) => void;
  onChange: (step: StepperStep) => void;
}

const EditStep = ({
  data,
  index,
  step,
  allSteps,
  selectedField,
  onFieldSelected,
  onChange,
}: EditStepProps) => {
  return (
    <div className="flex flex-col gap-4">
      <span className="text-md font-medium leading-8 text-gray-900">
        Editing Step {index}:
      </span>

      <QuillField
        title="Title"
        placeholder="Step Title"
        titlePosition="overlapping"
        value={step.title}
        onChange={(e) => {
          onChange({
            ...(step as any),
            title: extractTextFromQuillTrimmed(e as any) ? e : null,
          });
        }}
      />

      <QuillField
        title="Subtitle"
        placeholder="Step Subtitle"
        titlePosition="overlapping"
        value={step.subtitle}
        onChange={(e) => {
          onChange({
            ...(step as any),
            subtitle: extractTextFromQuillTrimmed(e as any) ? e : null,
          });
        }}
      />

      <QuillField
        title="Tagline"
        placeholder="Step Tagline"
        titlePosition="overlapping"
        value={step.tagline}
        onChange={(e) => {
          onChange({
            ...(step as any),
            tagline: extractTextFromQuillTrimmed(e as any) ? e : null,
          });
        }}
      />

      {(!step.stepType || step.stepType === 'multifield') && (
        <>
          <hr className="border-t border-gray-200" />

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Fields
              </label>
              <MenuButton
                items={formFieldTypes.map((type) => ({
                  id: type,
                  title: getFormFieldTypeLabel(type),
                  icon: getFormFieldTypeIcon(type),
                  onClick: () => {
                    const newField: FormField = {
                      id: uid(),
                      type,
                      label: getFormFieldTypeLabel(type),
                    };
                    onChange({
                      ...step,
                      fields: [...step.fields, newField],
                    });
                    onFieldSelected(step.fields.length);
                  },
                }))}
                customButton={
                  <IconButton variant="ternary" className="-me-1">
                    <PlusIcon className="h-4 w-4" />
                  </IconButton>
                }
              />
            </div>
            <SortableList
              hideDragHandle
              hideDivider
              items={step.fields}
              onChange={(items) => {
                onChange({
                  ...step,
                  fields: items,
                });
              }}
              renderItemContent={(item, index) => (
                <div
                  className={classNames(
                    'group',
                    'flex w-full cursor-pointer items-center justify-between rounded-md p-2 ring-1 ring-gray-200 transition hover:ring-2 hover:ring-blue-500',
                    selectedField?.id === item.id
                      ? 'bg-gray-200'
                      : 'hover:bg-gray-50',
                  )}
                  onClick={() => {
                    onFieldSelected(index);
                  }}
                >
                  <div className="flex items-center gap-3 text-sm">
                    <div className="flex h-6 min-w-6 items-center justify-center rounded-md bg-gray-100">
                      {getFormFieldTypeIcon(item.type)}
                    </div>
                    <div className="flex flex-1 flex-col">
                      <span className="text-gray-900">{item.label}</span>
                    </div>
                  </div>

                  <IconButton
                    color="negative"
                    variant="ternary"
                    className="absolute right-2 hidden group-hover:block"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange({
                        ...step,
                        fields: step.fields.filter((f) => f.id !== item.id),
                      });
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </IconButton>
                </div>
              )}
            />
          </div>
        </>
      )}

      {step.stepType === 'loading' && (
        <>
          <hr className="border-t border-gray-200" />

          <QuillField
            title="Loading Text"
            placeholder="Loading..."
            titlePosition="overlapping"
            value={step.loadingText}
            onChange={(e) => {
              onChange({
                ...step,
                loadingText: extractTextFromQuillTrimmed(e as any)
                  ? (e as any)
                  : null,
              });
            }}
          />
          <TextField
            type="number"
            title="Loading Duration (seconds)"
            titlePosition="overlapping"
            className="py-2"
            value={step.loadingDuration?.toString() || ''}
            onChange={(e) => {
              onChange({
                ...step,
                loadingDuration: e.target.valueAsNumber || 0,
              });
            }}
          />
        </>
      )}

      {(!step.stepType || step.stepType === 'multifield') && (
        <>
          <hr className="border-t border-gray-200" />

          <StepperStepLogic
            step={step}
            allSteps={allSteps}
            finalSteps={data.finalSteps || []}
            index={index - 1}
            onChange={(step) => {
              onChange({
                ...step,
                conditions: step.conditions?.map((c) => ({
                  ...c,
                  if: c.if.map((i) => ({
                    ...i,
                    field: step.stepType !== 'loading' ? i.field : '',
                  })),
                })),
              });
            }}
          />
        </>
      )}

      {(!step.stepType ||
        step.stepType === 'multifield' ||
        step.stepType === 'loading') && (
        <>
          <hr className="border-t border-gray-200" />

          <div className="flex items-center justify-between">
            <label
              htmlFor="email"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Advanced
            </label>
          </div>

          <QuillField
            title="Top Info Box"
            placeholder="Step Top Info Box"
            titlePosition="overlapping"
            value={step.topInfoBox}
            onChange={(e) => {
              onChange({
                ...step,
                topInfoBox: extractTextFromQuillTrimmed(e as any)
                  ? (e as any)
                  : null,
              });
            }}
          />
          <QuillField
            title="Bottom Text"
            placeholder="Step Bottom Text"
            titlePosition="overlapping"
            value={step.bottomText}
            onChange={(e) => {
              onChange({
                ...step,
                bottomText: extractTextFromQuillTrimmed(e as any)
                  ? (e as any)
                  : null,
              });
            }}
          />
          <QuillField
            title="Next Button Text"
            placeholder={
              extractTextFromQuillTrimmed(data.nextButtonText) || 'Next'
            }
            titlePosition="overlapping"
            value={step.nextButtonText || ''}
            onChange={(e) => {
              onChange({
                ...step,
                nextButtonText: extractTextFromQuillTrimmed(e as any)
                  ? (e as any)
                  : null,
              });
            }}
          />
          <QuillField
            title="Back Button Text"
            placeholder={
              extractTextFromQuillTrimmed(data.backButtonText) || 'Back'
            }
            titlePosition="overlapping"
            value={step.backButtonText || ''}
            onChange={(e) => {
              onChange({
                ...step,
                backButtonText: extractTextFromQuillTrimmed(e as any)
                  ? (e as any)
                  : null,
              });
            }}
          />
          <QuillField
            title="Submit Button Text"
            placeholder={
              extractTextFromQuillTrimmed(data.submitButtonText) || 'Submit'
            }
            titlePosition="overlapping"
            value={step.submitButtonText || ''}
            onChange={(e) => {
              onChange({
                ...step,
                submitButtonText: extractTextFromQuillTrimmed(e as any)
                  ? (e as any)
                  : null,
              });
            }}
          />
        </>
      )}

      {step.stepType === 'redirect' && (
        <>
          <hr className="border-t border-gray-200" />
          <TextField
            title="Redirect URL"
            titlePosition="overlapping"
            placeholder="https://example.com"
            value={step.redirectUrl || ''}
            onChange={(e) => {
              onChange({
                ...(step as any),
                redirectUrl: e.target.value,
              });
            }}
          />
        </>
      )}

      {step.stepType === 'iframe' && (
        <>
          <hr className="border-t border-gray-200" />
          <TextField
            title="Iframe URL"
            titlePosition="overlapping"
            placeholder="https://example.com"
            value={step.url || ''}
            onChange={(e) => {
              onChange({
                ...(step as any),
                url: e.target.value,
              });
            }}
          />
          <TextField
            type="number"
            title="Iframe Height (px)"
            titlePosition="overlapping"
            placeholder="300"
            value={step.height?.toString() || ''}
            onChange={(e) => {
              onChange({
                ...(step as any),
                height: e.target.valueAsNumber || 300,
              });
            }}
          />
        </>
      )}

      {step.stepType === 'customCode' && (
        <>
          <hr className="border-t border-gray-200" />
          <CodeEditorTextArea
            title="Custom Code"
            value={step.code || ''}
            className="h-56"
            allowModal
            onChange={(e) => {
              onChange({
                ...(step as any),
                code: e,
              });
            }}
          />
        </>
      )}
    </div>
  );
};

interface EditFieldProps {
  field: FormField;
  fields: FormField[];
  onChange: (field: FormField & { _id: string }) => void;
}

const EditField = ({ field, fields, onChange }: EditFieldProps) => {
  return (
    <div className="flex flex-col">
      <span className="text-md pb-3 font-medium leading-8 text-gray-900">
        Editing Field:
      </span>
      <FormFieldItemForm
        field={{ ...field, _id: field.id }}
        fields={fields.map((f) => ({
          ...f,
          _id: f.id,
        }))}
        readOnly={false}
        onChange={onChange}
        showAdvanced
      />
    </div>
  );
};

interface StepperStepLogicProps {
  step: MultiFieldStepperStep;
  allSteps: StepperStep[];
  finalSteps: FinalStepperStep[];
  index: number;
  onChange: (step: StepperStep) => void;
}

const StepperStepLogic = ({
  step,
  allSteps,
  finalSteps,
  index,
  onChange,
}: StepperStepLogicProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label
          htmlFor="email"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Logic
        </label>
        <IconButton
          variant="ternary"
          className="-me-1"
          onClick={() => {
            onChange({
              ...step,
              conditions: [
                ...(step.conditions || []),
                {
                  id: uid(),
                  if: [
                    {
                      id: uid(),
                      field: step.fields[0].id,
                      operator: 'equals',
                      value: null,
                    },
                  ],
                  then: {
                    step: allSteps[index + 1]?.id || '',
                  },
                },
              ],
            });
          }}
        >
          <PlusIcon className="h-4 w-4" />
        </IconButton>
      </div>

      <div className="flex flex-col gap-2">
        {step.conditions?.map((cond, index) => (
          <div
            key={cond.id}
            className="flex w-full cursor-pointer flex-col items-start justify-between gap-2 rounded-md p-2 ring-1 ring-gray-200 transition hover:ring-2 hover:ring-blue-500"
          >
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center justify-center">
                <span className="flex h-6 min-w-6 items-center justify-center rounded-md bg-gray-100 px-1">
                  Branch {index + 1}
                </span>
                <IconButton
                  color="negative"
                  size="small"
                  variant="ternary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({
                      ...step,
                      conditions: step.conditions?.filter(
                        (c) => c.id !== cond.id,
                      ),
                    });
                  }}
                >
                  <TrashIcon className="size-4" />
                </IconButton>
              </div>
            </div>
            {cond.if.map((condIf, index) => {
              const options = [
                ...step.fields.map((f) => ({
                  ...f,
                  subtitle: 'Current Step',
                })),
                ...(allSteps
                  .filter((s) => s.id !== step.id)
                  .flatMap((s) =>
                    'fields' in s
                      ? s.fields.map((f) => ({
                          ...f,
                          subtitle: `Step ${s.title || s.id}`,
                        }))
                      : [],
                  ) || []),
              ];

              const field = options.find((f) => f.id === condIf.field);
              return (
                <div key={condIf.id} className="w-full">
                  <div className="flex flex-col items-stretch gap-3 rounded-md p-2 text-sm ring-1 ring-gray-200">
                    <Autocomplete<
                      Pick<FormField, 'id' | 'label' | 'type'> & {
                        subtitle?: string;
                      }
                    >
                      selected={field}
                      options={options}
                      getValue={(o) => o.label}
                      getSubtitle={(o) => o.subtitle}
                      getIcon={(o) => getFormFieldTypeIcon(o.type)}
                      getOptionId={(o) => o.id}
                      onChange={(f) => {
                        console.log('Field changed', f);
                        const newConditions = step.conditions?.map((c) => {
                          if (c.id === cond.id) {
                            return {
                              ...c,
                              if: c.if.map((i) =>
                                i.id === condIf.id
                                  ? { ...i, field: f?.id || '' }
                                  : i,
                              ),
                            };
                          }
                          return c;
                        });
                        onChange({
                          ...step,
                          conditions: newConditions,
                        });
                      }}
                    />
                    {textOperatorFields.includes(field?.type ?? 'text') && (
                      <>
                        <Select
                          selected={condIf.operator}
                          options={textOperators.map((o) => ({
                            value: o.value,
                            child: o.label,
                          }))}
                          onChange={(o) => {
                            onChange({
                              ...step,
                              conditions: step.conditions?.map((c) => {
                                if (c.id === cond.id) {
                                  return {
                                    ...c,
                                    if: c.if.map((i) =>
                                      i.id === condIf.id
                                        ? { ...i, operator: o }
                                        : i,
                                    ),
                                  };
                                }
                                return c;
                              }),
                            });
                          }}
                        />
                        {condIf.operator !== 'isEmpty' && (
                          <TextField
                            placeholder="Value"
                            title="Value"
                            titlePosition="overlapping"
                            value={condIf.value}
                            onChange={(e) => {
                              onChange({
                                ...step,
                                conditions: step.conditions?.map((c) => {
                                  if (c.id === cond.id) {
                                    return {
                                      ...c,
                                      if: c.if.map((i) =>
                                        i.id === condIf.id
                                          ? { ...i, value: e.target.value }
                                          : i,
                                      ),
                                    };
                                  }
                                  return c;
                                }),
                              });
                            }}
                          />
                        )}
                      </>
                    )}
                    {numberOperatorFields.includes(field?.type ?? 'text') && (
                      <>
                        <Select
                          selected={condIf.operator}
                          options={numberOperators.map((o) => ({
                            value: o.value,
                            child: o.label,
                          }))}
                          onChange={(o) => {
                            onChange({
                              ...step,
                              conditions: step.conditions?.map((c) => {
                                if (c.id === cond.id) {
                                  return {
                                    ...c,
                                    if: c.if.map((i) =>
                                      i.id === condIf.id
                                        ? { ...i, operator: o }
                                        : i,
                                    ),
                                  };
                                }
                                return c;
                              }),
                            });
                          }}
                        />
                        <TextField
                          placeholder="Number Value"
                          title="Value"
                          type="number"
                          value={condIf.value}
                          onChange={(e) => {
                            onChange({
                              ...step,
                              conditions: step.conditions?.map((c) => {
                                if (c.id === cond.id) {
                                  return {
                                    ...c,
                                    if: c.if.map((i) =>
                                      i.id === condIf.id
                                        ? {
                                            ...i,
                                            value: e.target.valueAsNumber,
                                          }
                                        : i,
                                    ),
                                  };
                                }
                                return c;
                              }),
                            });
                          }}
                        />
                      </>
                    )}
                    {field && selectOparatorFields.includes(field.type) && (
                      <>
                        <Select
                          selected={condIf.operator}
                          options={selectOparators.map((o) => ({
                            value: o.value,
                            child: o.label,
                          }))}
                          onChange={(o) => {
                            onChange({
                              ...step,
                              conditions: step.conditions?.map((c) => {
                                if (c.id === cond.id) {
                                  return {
                                    ...c,
                                    if: c.if.map((i) =>
                                      i.id === condIf.id
                                        ? { ...i, operator: o }
                                        : i,
                                    ),
                                  };
                                }
                                return c;
                              }),
                            });
                          }}
                        />
                        {condIf.operator !== 'isEmpty' && (
                          <ChipsAutocomplete<{ value: string; label: string }>
                            title="Value"
                            getValue={(c) => c?.label}
                            selected={('options' in field
                              ? (field.options?.map((o) => {
                                  if (typeof o === 'string') {
                                    return { value: o, label: o };
                                  }
                                  return o;
                                }) ?? [])
                              : []
                            )?.filter((o) => {
                              if (condIf.value && Array.isArray(condIf.value)) {
                                return condIf.value.find(
                                  (v) => v.value === o.value,
                                );
                              }
                              return false;
                            })}
                            onChange={(s) => {
                              onChange({
                                ...step,
                                conditions: step.conditions?.map((c) => {
                                  if (c.id === cond.id) {
                                    return {
                                      ...c,
                                      if: c.if.map((i) =>
                                        i.id === condIf.id
                                          ? { ...i, value: s }
                                          : i,
                                      ),
                                    };
                                  }
                                  return c;
                                }),
                              });
                            }}
                            getOptionId={(t) =>
                              typeof t === 'string' ? t : t.value
                            }
                            options={
                              'options' in field
                                ? (field.options?.map((o) => {
                                    if (typeof o === 'string') {
                                      return { value: o, label: o };
                                    }
                                    return o;
                                  }) ?? [])
                                : []
                            }
                          />
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <span>AND</span>
                    {index + 1 < cond.if.length && (
                      <IconButton
                        color="negative"
                        size="small"
                        variant="ternary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange({
                            ...step,
                            conditions: step.conditions?.map((c) => {
                              if (c.id === cond.id) {
                                return {
                                  ...c,
                                  if: c.if.filter((_, i) => i !== index + 1),
                                };
                              }
                              return c;
                            }),
                          });
                        }}
                      >
                        <TrashIcon className="size-4" />
                      </IconButton>
                    )}
                  </div>
                </div>
              );
            })}

            <Button
              variant="secondary"
              icon={<PlusIcon className="h-4 w-4" />}
              onClick={() => {
                onChange({
                  ...step,
                  conditions: step.conditions?.map((c) => {
                    if (c.id === cond.id) {
                      return {
                        ...c,
                        if: [
                          ...c.if,
                          {
                            id: uid(),
                            field: step.fields[0].id,
                            operator: 'equals',
                            value: null,
                          },
                        ],
                      };
                    }
                    return c;
                  }),
                });
              }}
            >
              Add Rule
            </Button>

            <div className="flex items-center gap-1 pt-2">
              Then <ArrowRightIcon className="size-3" /> Go to:
            </div>
            <Autocomplete<StepperStep | FinalStepperStep | null>
              selected={
                [...allSteps, ...finalSteps].find(
                  (s) => s.id === cond.then.step,
                ) || null
              }
              options={[null, ...allSteps, ...finalSteps]}
              getValue={(o) =>
                o ? extractTextFromQuillTrimmed(o.title) || 'Step' : 'Next Step'
              }
              getOptionId={(o) => o?.id ?? 'next-step'}
              placeholder="Next Step"
              onChange={(s) => {
                onChange({
                  ...step,
                  conditions: step.conditions?.map((c) => {
                    if (c.id === cond.id) {
                      return {
                        ...c,
                        then: {
                          step: s?.id || '',
                        },
                      };
                    }
                    return c;
                  }),
                });
              }}
            />
          </div>
        ))}
        <div className="flex w-full cursor-pointer items-center justify-between rounded-md p-2 ring-1 ring-gray-200 transition hover:ring-2 hover:ring-blue-500">
          <div className="flex items-center gap-3 text-sm">
            <div className="flex h-6 min-w-6 items-center justify-center rounded-md bg-gray-100 px-1">
              {step.conditions?.length ? 'All other cases' : 'All cases'}
            </div>
            <div className="flex flex-1 flex-col">
              <span className="text-gray-900">Go to: Next step</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface GenerateFormProps {
  onFormGenerated?: (form: StepperForm) => void;
}

const GenerateForm = ({ onFormGenerated }: GenerateFormProps) => {
  const { selectedAccount } = useContext(UserContext);
  const { show } = useSnackbarContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aiLottieRef = useRef<LottieRefCurrentProps>(null);
  const manualLottieRef = useRef<LottieRefCurrentProps>(null);

  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState('');

  const handleGenerate = useCallback(async () => {
    if (!selectedAccount) return;

    if (!prompt) {
      show('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await generateStepperForm(selectedAccount.id, prompt)
        .then((res: any) => {
          if (onFormGenerated) {
            onFormGenerated(res);
          }
        })
        .catch((e) => {
          console.error(e);
          Sentry.captureException(e);
          setError(e.message || 'An error occurred while generating the form');
        });
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [onFormGenerated, prompt, selectedAccount, show]);

  useEffect(() => {
    if (aiLottieRef.current) {
      aiLottieRef.current.goToAndStop(30, true);
    }
    if (manualLottieRef.current) {
      manualLottieRef.current.goToAndStop(85, true);
    }
  }, []);

  return (
    <div className="mx-auto grid grid-cols-1 gap-6 p-6 sm:grid-cols-2">
      <div
        className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-4 shadow hover:shadow-lg"
        onClick={() => onFormGenerated?.(defaultForm())}
        onMouseEnter={() => {
          if (manualLottieRef.current) {
            const anim = manualLottieRef.current.animationItem;
            if (!anim) return;

            anim.loop = true;
            anim.resetSegments(true);
            anim.goToAndPlay(85, true);
          }
        }}
        onMouseLeave={() => {
          if (manualLottieRef.current) {
            const anim = manualLottieRef.current.animationItem;
            if (!anim) return;

            anim.loop = false;
            const currentFrame = anim.currentFrame;
            anim.playSegments([currentFrame, 85], true);
          }
        }}
      >
        <div className="mb-4 h-40 w-full">
          <Lottie
            animationData={penDrawingLottie}
            className="h-full w-full"
            autoplay={false}
            lottieRef={manualLottieRef}
          />
        </div>
        <span className="text-lg font-medium">Start from scratch</span>
      </div>
      <div
        className={classNames(
          'flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white p-4 shadow hover:shadow-lg',
          showPrompt && 'ring-1 ring-indigo-600',
        )}
        onClick={() => setShowPrompt(!showPrompt)}
        onMouseEnter={() => {
          if (aiLottieRef.current) {
            const anim = aiLottieRef.current.animationItem;
            if (!anim) return;

            anim.loop = true;
            anim.resetSegments(true);
            anim.goToAndPlay(30, true);
          }
        }}
        onMouseLeave={() => {
          if (aiLottieRef.current) {
            const anim = aiLottieRef.current.animationItem;
            if (!anim) return;

            anim.loop = false;
            const currentFrame = anim.currentFrame;
            anim.playSegments([currentFrame, 30], true);
          }
        }}
      >
        <div className="mb-4 h-40 w-full">
          <Lottie
            lottieRef={aiLottieRef}
            animationData={rocketFlyingLottie}
            autoplay={false}
            className="h-full w-full"
          />
        </div>
        <span className="text-lg font-medium">Generate using AI</span>
      </div>
      {showPrompt && (
        <div className="col-span-full flex flex-col">
          <TextArea
            placeholder="Describe the stepper you want to create"
            className="w-full"
            value={prompt}
            readOnly={loading}
            onChange={(e) => {
              setPrompt(e.target.value);
            }}
          />

          <Button
            className="mt-4 w-full"
            loading={loading}
            onClick={handleGenerate}
          >
            Generate
          </Button>

          {error && (
            <div className="col-span-full mt-4 text-center text-red-600">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface PromptBoxProps {
  data: StepperForm;
  onGenerated: (generated: StepperForm) => void;
}

const PromptBox = ({ data, onGenerated }: PromptBoxProps) => {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [prev, setPrev] = useState<StepperForm | null>(null);
  const [generated, setGenerated] = useState(false);

  const handleGenerate = async () => {
    if (!selectedAccount) return;
    if (!prompt) {
      show('Please enter a prompt');
      return;
    }

    if (prompt.length < 5) {
      show('Prompt must be at least 5 characters long');
      return;
    }

    setLoading(true);
    setError(null);
    setPrev(data);
    setGenerated(false);
    try {
      await generateStepperForm(selectedAccount.id, prompt, data)
        .then((res: any) => {
          onGenerated(res);
          setGenerated(true);
        })
        .catch((e) => {
          console.error(e);
          Sentry.captureException(e);
          setError(e.message || 'An error occurred while generating the form');
        });
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setPrev(null);
    setGenerated(false);
    setPrompt('');
  };

  const handleDiscard = async () => {
    onGenerated(prev || data);
    setPrev(null);
    setGenerated(false);
  };

  return (
    <div className="my-1">
      <TextArea
        title="AI Edit"
        placeholder="Prompt to change the stepper form"
        className="w-full"
        value={prompt}
        readOnly={loading}
        disabled={generated}
        onChange={(e) => {
          setPrompt(e.target.value);
        }}
        onCmdEnter={handleGenerate}
      />

      {!generated && (
        <Button
          className="mt-3 w-full"
          loading={loading}
          onClick={handleGenerate}
          icon={<SparklesIcon className="h-4 w-4" />}
        >
          Edit
        </Button>
      )}

      {generated && (
        <div className="flex gap-2">
          <Button
            className="mt-3 w-full"
            variant="secondary"
            loading={loading}
            onClick={handleDiscard}
          >
            Discard
          </Button>
          <Button
            className="mt-3 w-full"
            color="positive"
            loading={loading}
            onClick={handleAccept}
            icon={<CheckIcon className="h-4 w-4" />}
          >
            Accept
          </Button>
        </div>
      )}

      {error && <div className="mt-4 text-center text-red-600">{error}</div>}
    </div>
  );
};

interface LogicTabProps {
  data: StepperForm;
  setData: (_: SetStateAction<StepperForm | null>) => void;
}

const LogicTab = ({ data }: LogicTabProps) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const updateNodesAndEdges = useCallback(() => {
    // Build nodes and edges from the form steps and their logic
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    // Create nodes for all steps
    data.steps.forEach((step, index) => {
      newNodes.push({
        id: step.id,
        type: 'step',
        data: {
          label: 'Step ' + (index + 1),
          index,
          step,
          hideTargetHandle: index === 0, // Hide target handle for the first step
        },
        position: { x: 0, y: 0 }, // Positions will be calculated by dagre layout
        sourcePosition: Position.Right, // For LR layout: outputs on right side
        targetPosition: Position.Left, // Hide targetPosition on first step
      });
    });

    // Create edges for the default flow (step to next step)
    data.steps.forEach((step, index) => {
      if (index < data.steps.length - 1) {
        newEdges.push({
          id: `default-edge-${step.id}-${data.steps[index + 1].id}`,
          source: step.id,
          target: data.steps[index + 1].id,
          animated: false,
          type: 'smoothstep',
          style: { stroke: '#888' },
          label: 'Default',
          labelStyle: { fill: '#888', fontSize: 12 },
        });
      }
    });

    (data.finalSteps || []).forEach((step, index) => {
      newNodes.push({
        id: step.id,
        type: 'step',
        data: {
          label: 'Final Step ' + (index + 1),
          index,
          step,
          hideTargetHandle: false,
          hideSourceHandle: true,
        },
        position: { x: 0, y: 0 }, // Positions will be calculated by dagre layout
        sourcePosition: Position.Right, // For LR layout: outputs on right side
        targetPosition: Position.Left, // Hide targetPosition on first step
      });
    });

    // Create conditional branches
    data.steps.forEach((step, index) => {
      let added = false;
      if (step.conditions) {
        step.conditions.forEach((condition, condIndex) => {
          // Find target step
          const targetStep =
            data.steps.find((s) => s.id === condition.then.step) ||
            data.finalSteps?.find((s) => s.id === condition.then.step);
          if (targetStep) {
            // Add edge for conditional flow
            newEdges.push({
              id: `condition-${step.id}-${condition.id}-${targetStep.id}`,
              source: step.id,
              target: targetStep.id,
              animated: false,
              type: 'smoothstep',
              label: `Branch ${condIndex + 1}`,
              labelStyle: { fill: '#555', fontSize: 12 },
            });

            added = true;
          }
        });
      }

      const isLastStep = index === data.steps.length - 1;

      if (isLastStep && !added) {
        // If no conditions, connect to next step
        const finalStep = data.finalSteps?.[0];
        if (finalStep) {
          newEdges.push({
            id: `default-edge-${step.id}-${finalStep.id}`,
            source: step.id,
            target: finalStep.id,
            animated: false,
            type: 'smoothstep',
            label: 'Default',
            labelStyle: { fill: '#888', fontSize: 12 },
          });
        }
      }
    });

    // Auto-layout nodes and edges
    const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(
      () => ({}),
    );
    const nodeWidth = 200;
    const nodeHeight = 100;

    dagreGraph.setGraph({
      rankdir: 'LR',
      nodesep: 200,
      ranksep: 200,
      marginx: 20,
      marginy: 20,
      align: 'UL',
      ranker: 'longest-path',
    });

    // Add nodes to dagre graph
    newNodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    // Add edges to dagre graph
    newEdges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculate layout
    dagre.layout(dagreGraph, {});

    // Apply layout to nodes
    const layoutedNodes = newNodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - nodeWidth / 2,
          y: nodeWithPosition.y - nodeHeight / 2,
        },
      };
    });

    setNodes(layoutedNodes);
    setEdges(newEdges);
  }, [data.steps, setNodes, setEdges, data.finalSteps]);

  useEffect(() => {
    if (data) {
      updateNodesAndEdges();
    }
  }, [data, updateNodesAndEdges]);

  return (
    <div className="h-full w-full overflow-hidden bg-gray-50 px-2 pb-2">
      <div className="h-full w-full">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          // disable user‐created connections
          onConnect={() => {}}
          nodesConnectable={false}
          // keep draggable nodes
          nodesDraggable={true}
          nodeTypes={{ step: StepNode }}
          fitView
          attributionPosition="bottom-right"
          style={{ backgroundColor: '#f8fafc' }}
          minZoom={0.1}
          fitViewOptions={{ padding: 0.2 }}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
        <Button
          className="absolute bottom-4 right-4 z-10"
          variant="secondary"
          onClick={updateNodesAndEdges}
          icon={<ArrowPathIcon className="h-4 w-4" />}
        >
          Recalculate Layout
        </Button>
      </div>
    </div>
  );
};

const StepNode = ({
  data,
}: {
  data: {
    label: string;
    index: number;
    step: StepperStep;
    hideTargetHandle?: boolean;
    hideSourceHandle?: boolean;
  };
}) => {
  const { hideTargetHandle, hideSourceHandle, label, step } = data;

  return (
    <div className="h-28 w-[300px] rounded-lg border-2 border-indigo-500 bg-white p-4 shadow-lg">
      {!hideTargetHandle && <Handle type="target" position={Position.Left} />}
      <div className="absolute -top-8 left-0">
        <strong>{label}</strong>
      </div>
      <div className="mb-2 text-lg font-bold text-indigo-600">
        {extractTextFromQuillTrimmed(step.title)}
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="font-medium text-gray-600">
            {extractTextFromQuillTrimmed(step.subtitle)}
          </span>
        </div>
      </div>
      {!hideSourceHandle && <Handle type="source" position={Position.Right} />}
    </div>
  );
};

interface SettingsTabProps {
  data: StepperForm;
  setData: (_: SetStateAction<StepperForm | null>) => void;
}

const SettingsTab = ({ data, setData }: SettingsTabProps) => {
  return (
    <div className="h-full w-full overflow-hidden px-2 pb-2">
      <CardWrapper>
        <div className="flex-1">
          <div className="flex items-center justify-between pb-2">
            <label
              htmlFor="email"
              className="text-md block font-medium leading-8 text-gray-900"
            >
              Settings
            </label>
          </div>
          <div className="flex flex-col gap-2">
            <QuillField
              title="Default Next Button Text"
              placeholder="Next"
              value={data.nextButtonText}
              onChange={(e) => {
                setData((prev) =>
                  prev
                    ? {
                        ...prev,
                        nextButtonText: extractTextFromQuillTrimmed(e)
                          ? (e as any)
                          : '',
                      }
                    : prev,
                );
              }}
            />
            <QuillField
              title="Default Back Button Text"
              placeholder="Back"
              value={data.backButtonText}
              onChange={(e) => {
                setData((prev) =>
                  prev
                    ? {
                        ...prev,
                        backButtonText: extractTextFromQuillTrimmed(e)
                          ? (e as any)
                          : '',
                      }
                    : prev,
                );
              }}
            />
            <QuillField
              title="Default Submit Button Text"
              placeholder="Submit"
              value={data.submitButtonText}
              onChange={(e) => {
                setData((prev) =>
                  prev
                    ? {
                        ...prev,
                        submitButtonText: extractTextFromQuillTrimmed(e)
                          ? (e as any)
                          : '',
                      }
                    : prev,
                );
              }}
            />
          </div>
        </div>
      </CardWrapper>
    </div>
  );
};

interface PreviewTabProps {
  formId?: string;
  data: StepperForm;
}

const PreviewTab = ({ formId, data }: PreviewTabProps) => {
  const [ready, setReady] = useState(false);
  const [id] = useState<string>(`stepper-preview-${uid()}`);
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!data || !ready) return;

    sendMessageToIframe(
      {
        eventName: 'updated',
        form: data,
        formId: formId || '',
        website: {},
      },
      ref,
    );
  }, [data, formId, ready]);

  const handlePreviewEvent = useCallback(
    (event: MessageEvent<string>) => {
      if (event.origin !== PREVIEW_ROOT) return;

      const iframe = (document.getElementById(id) as HTMLIFrameElement)
        ?.contentWindow;
      if (event.source !== iframe) return;

      try {
        const data = JSON.parse(event.data);
        console.log('[WEBSITE_UPDATER] event', data?.eventName, data);

        if (data.eventName === 'ready') {
          console.warn(
            '[WEBSITE_PREVIEW_EVENT] ready event recieved',
            data.eventName,
          );
          setReady(true);
          return;
        }
      } catch (e) {
        Sentry.captureException(e);
        console.warn('[WEBSITE_PREVIEW_EVENT] failed to parse event data', e);
      }
    },
    [id],
  );

  useEffect(() => {
    window.addEventListener('message', handlePreviewEvent);

    return () => {
      window.removeEventListener('message', handlePreviewEvent);
    };
  }, [handlePreviewEvent]);

  return (
    <div className="h-full w-full overflow-hidden pb-2">
      <iframe
        ref={ref}
        id={id}
        src={STEPPER_PREVIEW_URL}
        className="h-full w-full border-0"
        title="Stepper Preview"
      />
    </div>
  );
};
